import { createSlice, createSelector } from "@reduxjs/toolkit";
import type { RootState } from "./UiDb";
import { BusinessSectionNode } from "../../interfaces/BusinessSectionObject";
import { findSectionById } from "../../services/helpers/BusinessSectionTreeHelper";
import {
  fetchSectionTree,
  createSection,
  renameOrMoveSection,
  deleteSection,
  reorderSections,
} from "../../services/thunks/BusinessSectionsThunk";
import { resetAllState } from "./resetAllState";

interface BusinessSectionsState {
  /**
   * One business's full tree, cached by businessId once fetched — every
   * consumer (the product-form picker, the sections manager, bulk-upload
   * flows) shares this one entry instead of each holding its own copy.
   *
   * `undefined` means "never fetched"; an empty array `[]` means the fetch
   * succeeded and the business genuinely has zero sections — those are NOT
   * the same thing. Don't rely on that distinction alone for "is this
   * loaded" without also checking `successMap` below: nothing today pre-
   * seeds this map, but a future optimistic-insert path easily could, and at
   * that point "has an entry" stops implying "has been fetched."
   */
  treesByBusinessId: Record<string, BusinessSectionNode[]>;
  /**
   * loadingMap / errorMap / successMap for `fetchSectionTree` ONLY, keyed by
   * businessId — same three-map shape every other slice uses (see
   * BusinessCollectionDb.ts), just keyed by businessId instead of action name
   * since this slice's one tracked async action is inherently per-business.
   *
   * Deliberately NOT extended to create/rename/delete/reorder: each of those
   * is triggered by one button/row, which awaits its own dispatch via
   * `.unwrap()` and owns its own local "isSaving" state — the same pattern
   * SectionSelect/SubSectionSelect already use for the legacy flow. A
   * mutation's loading/error state belongs to the control that triggered it,
   * not to the whole cached tree, so giving this slice four more parallel
   * loading/error/success maps would track state nothing reads.
   */
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: BusinessSectionsState = {
  treesByBusinessId: {},
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

// ─── Pure tree-mutation helpers ─────────────────────────────────────────────
// Operate on a plain BusinessSectionNode[] and return a new one, so the
// reducer cases below stay one-liners. Kept outside the slice (not inline in
// extraReducers) so they're easy to reason about and unit-test in isolation.
//
// Each one preserves REFERENCE EQUALITY for every subtree it doesn't touch:
// a mutation only allocates new objects along the path from the root down to
// the changed node, and returns the ORIGINAL array/node reference for every
// sibling not on that path. Without this, every mutation would deep-clone the
// entire tree (every business's every section, at every depth) just to
// change one node — the actual scalability ceiling for a feature whose whole
// point is unlimited depth. This also means a memoized consumer (e.g. a
// React.memo'd row keyed on its node) can skip re-rendering subtrees that
// didn't change, instead of every node re-rendering on every mutation.

function insertSection(
  tree: BusinessSectionNode[],
  parentSectionId: string | null,
  node: BusinessSectionNode
): BusinessSectionNode[] {
  if (parentSectionId === null) return [...tree, node];
  let changed = false;
  const next = tree.map((n) => {
    if (n.sectionId === parentSectionId) {
      changed = true;
      return { ...n, children: [...n.children, node] };
    }
    if (n.children.length === 0) return n;
    const updatedChildren = insertSection(n.children, parentSectionId, node);
    if (updatedChildren === n.children) return n;
    changed = true;
    return { ...n, children: updatedChildren };
  });
  return changed ? next : tree;
}

function removeSection(tree: BusinessSectionNode[], sectionId: string): BusinessSectionNode[] {
  const filtered = tree.filter((n) => n.sectionId !== sectionId);
  let changed = filtered.length !== tree.length;
  const next = filtered.map((n) => {
    if (n.children.length === 0) return n;
    const updatedChildren = removeSection(n.children, sectionId);
    if (updatedChildren === n.children) return n;
    changed = true;
    return { ...n, children: updatedChildren };
  });
  return changed ? next : tree;
}

function replaceSectionFields(
  tree: BusinessSectionNode[],
  sectionId: string,
  updated: BusinessSectionNode
): BusinessSectionNode[] {
  let changed = false;
  const next = tree.map((n) => {
    if (n.sectionId === sectionId) {
      changed = true;
      return { ...updated, children: n.children };
    }
    if (n.children.length === 0) return n;
    const updatedChildren = replaceSectionFields(n.children, sectionId, updated);
    if (updatedChildren === n.children) return n;
    changed = true;
    return { ...n, children: updatedChildren };
  });
  return changed ? next : tree;
}

/** Splices `newSiblings` in as the `children` of the node identified by
 *  `parentSectionId`, wherever it sits in the tree — same reference-
 *  preserving traversal as the helpers above. */
function spliceSiblingsUnderParent(
  nodes: BusinessSectionNode[],
  parentSectionId: string,
  newSiblings: BusinessSectionNode[]
): BusinessSectionNode[] {
  let changed = false;
  const next = nodes.map((n) => {
    if (n.sectionId === parentSectionId) {
      changed = true;
      return { ...n, children: newSiblings };
    }
    if (n.children.length === 0) return n;
    const updatedChildren = spliceSiblingsUnderParent(n.children, parentSectionId, newSiblings);
    if (updatedChildren === n.children) return n;
    changed = true;
    return { ...n, children: updatedChildren };
  });
  return changed ? next : nodes;
}

/**
 * Applies a rename/move API response to the cached tree. A plain rename
 * (parentSectionId unchanged) replaces the node's fields IN PLACE, preserving
 * its position among siblings — a rename must never silently reorder it. An
 * actual move (parentSectionId changed) splices it out and appends it under
 * the new parent; the move payload carries no displayOrder for its new
 * position, so a follow-up reorder is how a curator would fix that up. Either
 * way the node's existing children are preserved — the rename/move response
 * only carries the node's own fields, never its subtree.
 */
function applyRenamedOrMovedSection(
  tree: BusinessSectionNode[],
  updated: BusinessSectionNode
): BusinessSectionNode[] {
  const existing = findSectionById(tree, updated.sectionId);
  const nextNode: BusinessSectionNode = { ...updated, children: existing?.children ?? [] };

  if (existing && existing.parentSectionId === nextNode.parentSectionId) {
    return replaceSectionFields(tree, nextNode.sectionId, nextNode);
  }
  return insertSection(
    removeSection(tree, nextNode.sectionId),
    nextNode.parentSectionId,
    nextNode
  );
}

/**
 * Applies a bulk-reorder response for one sibling group. The response only
 * carries each sibling's own fields (id/name/parentSectionId/displayOrder),
 * not its subtree, so each sibling's existing children are re-attached from
 * the current cache before the group is spliced back in — otherwise
 * reordering a section would silently wipe out its subsections.
 */
function applyReorderedSiblings(
  tree: BusinessSectionNode[],
  parentSectionId: string | null,
  siblings: BusinessSectionNode[]
): BusinessSectionNode[] {
  const currentSiblings =
    parentSectionId === null ? tree : findSectionById(tree, parentSectionId)?.children ?? [];
  const childrenById = new Map(currentSiblings.map((n) => [n.sectionId, n.children]));
  const withChildren = siblings.map((n) => ({
    ...n,
    children: childrenById.get(n.sectionId) ?? n.children,
  }));

  if (parentSectionId === null) return withChildren;
  return spliceSiblingsUnderParent(tree, parentSectionId, withChildren);
}

const businessSectionsSlice = createSlice({
  name: "businessSections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSectionTree.pending, (state, action) => {
        state.loadingMap[action.meta.arg] = "Loading sections...";
        state.errorMap[action.meta.arg] = null;
        state.successMap[action.meta.arg] = false;
      })
      .addCase(fetchSectionTree.fulfilled, (state, action) => {
        const { businessId, tree } = action.payload;
        state.loadingMap[businessId] = null;
        state.successMap[businessId] = true;
        state.treesByBusinessId[businessId] = tree;
      })
      .addCase(fetchSectionTree.rejected, (state, action) => {
        state.loadingMap[action.meta.arg] = null;
        state.errorMap[action.meta.arg] = action.payload as string;
      });

    builder.addCase(createSection.fulfilled, (state, action) => {
      const { businessId, created } = action.payload;
      const tree = state.treesByBusinessId[businessId];
      // Nothing cached yet for this business — the next fetchSectionTree
      // dispatch already comes back correct, this section included.
      // Inserting into an empty stand-in here would create a fake
      // single-entry cache that looks loaded and permanently skips that
      // real fetch (same reasoning as rename/delete/reorder below).
      if (!tree) return;
      state.treesByBusinessId[businessId] = insertSection(
        tree,
        created.parentSectionId,
        created
      );
    });

    builder.addCase(renameOrMoveSection.fulfilled, (state, action) => {
      const { businessId, updated } = action.payload;
      const tree = state.treesByBusinessId[businessId];
      // Nothing cached yet for this business — the next fetchSectionTree
      // dispatch will already come back correct, nothing to patch here.
      if (!tree) return;
      state.treesByBusinessId[businessId] = applyRenamedOrMovedSection(tree, updated);
    });

    builder.addCase(deleteSection.fulfilled, (state, action) => {
      const { businessId, sectionId } = action.payload;
      const tree = state.treesByBusinessId[businessId];
      if (!tree) return;
      state.treesByBusinessId[businessId] = removeSection(tree, sectionId);
    });

    builder.addCase(reorderSections.fulfilled, (state, action) => {
      const { businessId, parentSectionId, siblings } = action.payload;
      const tree = state.treesByBusinessId[businessId];
      if (!tree) return;
      state.treesByBusinessId[businessId] = applyReorderedSiblings(
        tree,
        parentSectionId,
        siblings
      );
    });

    builder.addCase(resetAllState, () => initialState);
  },
});

export default businessSectionsSlice.reducer;

// ─── Selectors ───────────────────────────────────────────────────────────────

const selectSlice = (state: RootState) => state.businessSections;

/**
 * A business's cached tree, or `undefined` if it's never been fetched. Note
 * this is NOT the same as "loaded" on its own — an empty array is a valid,
 * fully-loaded result (a business with zero sections). Pair with
 * `selectSectionTreeStatus` when you need to tell "still loading" apart from
 * "loaded and empty."
 */
export const selectSectionTree = createSelector(
  [selectSlice, (_: RootState, businessId: string) => businessId],
  (slice, businessId): BusinessSectionNode[] | undefined => slice.treesByBusinessId[businessId]
);

// Three plain (non-memoized) selectors instead of one object-returning
// `createSelector` — that selector was shared across every SectionTreePicker
// instance on a page, each calling it with a DIFFERENT businessId. reselect's
// default cache holds exactly one result, so those interleaved calls thrashed
// it constantly, and every miss built a brand-new object — breaking
// `useSelector`'s reference-equality check and re-rendering every picker on
// ANY business's status changing, not just its own. Each of these returns a
// primitive, so equality works correctly regardless of how many businessIds
// call it, with no memoization needed for a plain property lookup this cheap.
export const selectSectionTreeIsLoading = (state: RootState, businessId: string): boolean =>
  Boolean(state.businessSections.loadingMap[businessId]);

export const selectSectionTreeHasLoaded = (state: RootState, businessId: string): boolean =>
  Boolean(state.businessSections.successMap[businessId]);

export const selectSectionTreeError = (state: RootState, businessId: string): string | null =>
  state.businessSections.errorMap[businessId] ?? null;
